import React, { useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { X, Trophy, Zap, Brain, Award, Waves, Timer, Flame, Radio, Library, Clock, Headphones, Footprints } from 'lucide-react';

const ICON_MAP = {
  Footprints, Zap, Brain, Award, Waves, Timer, Flame, Radio, Library, Clock, Headphones, Trophy,
};

const ACHIEVEMENTS = [
  { id: 'first_steps', name: 'First Steps', desc: 'Complete your first session', icon: 'Footprints', target: 1, type: 'sessions', color: '#8B5CF6' },
  { id: 'getting_started', name: 'Getting Started', desc: 'Complete 5 sessions', icon: 'Zap', target: 5, type: 'sessions', color: '#3B82F6' },
  { id: 'dedicated_mind', name: 'Dedicated Mind', desc: 'Complete 25 sessions', icon: 'Brain', target: 25, type: 'sessions', color: '#EC4899' },
  { id: 'century_club', name: 'Century Club', desc: 'Complete 100 sessions', icon: 'Award', target: 100, type: 'sessions', color: '#F59E0B' },
  { id: 'deep_diver', name: 'Deep Diver', desc: 'Complete a 30+ min session', icon: 'Waves', target: 30, type: 'longest_session_min', color: '#06B6D4' },
  { id: 'marathon_mind', name: 'Marathon Mind', desc: 'Complete a 60+ min session', icon: 'Timer', target: 60, type: 'longest_session_min', color: '#10B981' },
  { id: 'streak_starter', name: 'Streak Starter', desc: 'Maintain a 3-day streak', icon: 'Flame', target: 3, type: 'streak', color: '#EF4444' },
  { id: 'streak_master', name: 'Streak Master', desc: 'Maintain a 7-day streak', icon: 'Flame', target: 7, type: 'streak', color: '#F97316' },
  { id: 'frequency_explorer', name: 'Frequency Explorer', desc: 'Use all 5 beat types', icon: 'Radio', target: 5, type: 'unique_beat_types', color: '#A855F7' },
  { id: 'preset_collector', name: 'Preset Collector', desc: 'Use 10 different presets', icon: 'Library', target: 10, type: 'unique_presets', color: '#14B8A6' },
  { id: 'time_traveler', name: 'Time Traveler', desc: 'Accumulate 10 hours listening', icon: 'Clock', target: 600, type: 'total_minutes', color: '#6366F1' },
  { id: 'sound_pioneer', name: 'Sound Pioneer', desc: 'Accumulate 50 hours listening', icon: 'Headphones', target: 3000, type: 'total_minutes', color: '#D946EF' },
];

function computeProgress(history) {
  if (!history || history.length === 0) {
    return { sessions: 0, total_minutes: 0, longest_session_min: 0, streak: 0, unique_beat_types: 0, unique_presets: 0 };
  }

  const sessions = history.length;
  const total_minutes = history.reduce((sum, s) => sum + Math.round((s.duration || 0) / 60), 0);
  const longest_session_min = Math.round(Math.max(...history.map(s => (s.duration || 0) / 60)));

  const beatTypes = new Set(history.map(s => s.beatType).filter(Boolean));
  const presets = new Set(history.map(s => s.preset).filter(p => p && p !== 'Custom' && p !== 'Custom Session'));

  // Calculate streak
  const dateSet = new Set(history.map(s => new Date(s.date).toDateString()));
  const dates = [...dateSet].map(d => new Date(d)).sort((a, b) => b - a);
  let streak = 0;
  if (dates.length > 0) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const first = new Date(dates[0]);
    first.setHours(0, 0, 0, 0);
    const daysSinceLatest = Math.floor((today - first) / 86400000);
    if (daysSinceLatest <= 1) {
      streak = 1;
      for (let i = 1; i < dates.length; i++) {
        const prev = new Date(dates[i - 1]);
        prev.setHours(0, 0, 0, 0);
        const curr = new Date(dates[i]);
        curr.setHours(0, 0, 0, 0);
        const diff = Math.floor((prev - curr) / 86400000);
        if (diff === 1) streak++;
        else break;
      }
    }
  }

  return { sessions, total_minutes, longest_session_min, streak, unique_beat_types: beatTypes.size, unique_presets: presets.size };
}

const AchievementsPanel = ({ history, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const progress = useMemo(() => computeProgress(history), [history]);

  const achievements = useMemo(() => {
    return ACHIEVEMENTS.map(a => {
      const current = progress[a.type] || 0;
      const pct = Math.min(100, Math.round((current / a.target) * 100));
      const unlocked = current >= a.target;
      return { ...a, current, pct, unlocked };
    });
  }, [progress]);

  const unlockedCount = achievements.filter(a => a.unlocked).length;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="achievements-panel">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 to-slate-800 border-white/20">
        <CardHeader className="relative">
          <Button onClick={onClose} variant="ghost" size="sm" aria-label="Close achievements" className="absolute top-4 right-4 text-white hover:bg-white/10" data-testid="achievements-close-btn">
            <X className="w-5 h-5" />
          </Button>
          <CardTitle className="text-white text-2xl flex items-center gap-3">
            <Trophy className="w-7 h-7 text-yellow-400" />
            Achievements
            <span className="text-base font-normal text-white/60 ml-2">{unlockedCount}/{ACHIEVEMENTS.length} unlocked</span>
          </CardTitle>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {achievements.map(a => {
              const Icon = ICON_MAP[a.icon] || Trophy;
              return (
                <div
                  key={a.id}
                  data-testid={`achievement-${a.id}`}
                  className={`relative rounded-xl p-4 border transition-all duration-300 ${
                    a.unlocked
                      ? 'border-yellow-500/40 bg-gradient-to-br from-yellow-500/10 to-amber-600/5'
                      : 'border-white/10 bg-white/5 opacity-70'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${a.unlocked ? '' : 'grayscale'}`}
                      style={{ backgroundColor: a.unlocked ? `${a.color}30` : 'rgba(255,255,255,0.1)' }}
                    >
                      <Icon className="w-5 h-5" style={{ color: a.unlocked ? a.color : 'rgba(255,255,255,0.4)' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-white text-sm font-semibold truncate">{a.name}</h4>
                        {a.unlocked && <span className="text-yellow-400 text-xs font-bold">UNLOCKED</span>}
                      </div>
                      <p className="text-white/50 text-xs mt-0.5">{a.desc}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <Progress value={a.pct} className="h-1.5 flex-1" />
                        <span className="text-white/40 text-xs whitespace-nowrap">
                          {a.type === 'total_minutes'
                            ? `${Math.round(a.current / 60)}/${Math.round(a.target / 60)}h`
                            : `${a.current}/${a.target}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export { ACHIEVEMENTS, computeProgress };
export default AchievementsPanel;
